import { useEffect } from "react";
import { Mail, MailOpen, Trash2 } from "lucide-react";
import {
  PageHeader,
  PageLayout,
  PageNotice,
  PageSection,
  PageState,
} from "@/layout/page-layout";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/confirm-dialog";
import { NotificationItem } from "@/components/notification-item";
import { NotificationsEmpty } from "@/components/notifications-empty";
import { useNotificationsInbox } from "@/hooks/use-notifications-inbox";
import { ApiError } from "@/lib/api-client";
import type { CategoryCount } from "@/lib/autar/notifications";
import { useI18n } from "@/lib/i18n";
import { enumParam, textParam, useUrlState } from "@/lib/routing/url-state";
import {
  categoryKey,
  renderNotification,
} from "@/features/notifications/renderers";
import "@/components/notifications-page.css";
import { trackNotifications } from "@/features/notifications/analytics";

const VIEW = {
  filter: enumParam(["all", "unread", "read"] as const, "all"),
  category: textParam(),
};

const isAuthFailure = (error: unknown) =>
  error instanceof ApiError && (error.status === 401 || error.status === 403);

export function NotificationsPage() {
  const { t, locale, formatNumber } = useI18n();
  const [{ filter, category }, setView] = useUrlState(VIEW);
  const inbox = useNotificationsInbox(filter, category);
  const confirm = useConfirm();
  const sum = (pick: (c: CategoryCount) => number) =>
    inbox.categories.reduce((total, c) => total + pick(c), 0);
  const tabs = [
    {
      id: "all",
      label: t("notifications.filter.all"),
      count: sum((c) => c.total),
      view: { filter: "all" as const, category: "" },
    },
    {
      id: "unread",
      label: t("notifications.filter.unread"),
      count: sum((c) => c.unread),
      view: { filter: "unread" as const, category: "" },
    },
    ...inbox.categories.map((c) => ({
      id: c.category,
      label: t(categoryKey(c.category)),
      count: c.total,
      view: { filter: "all" as const, category: c.category },
    })),
  ];
  const active = category || (filter === "unread" ? "unread" : "all");
  const remove = async (id: string, title: string) => {
    const confirmed = await confirm({
      title: t("notifications.delete.title"),
      description: title,
      confirmLabel: t("notifications.delete"),
      destructive: true,
    });
    if (confirmed) inbox.remove(id);
  };
  useEffect(() => trackNotifications("page", "open"), []);

  return (
    <PageLayout className="notifications-page">
      <PageHeader
        eyebrow={t("notifications.page.eyebrow")}
        title={t("notifications.page.title")}
        description={t("notifications.page.description")}
        action={
          <Button
            variant="outline"
            onClick={inbox.markAllRead}
            disabled={inbox.loading || inbox.items.length === 0}
          >
            {t("notifications.markAllRead")}
          </Button>
        }
      />
      <PageSection>
        <h2 className="sr-only">{t("notifications.panel.title")}</h2>
        <nav
          className="notifications-tabs"
          aria-label={t("notifications.tabs.label")}
        >
          {tabs.map((tab) => (
            <Button
              key={tab.id}
              variant={tab.id === active ? "secondary" : "ghost"}
              size="inline"
              aria-pressed={tab.id === active}
              onClick={() => setView(tab.view, { viewTransition: true })}
            >
              {tab.label}{" "}
              <span className="notifications-tab-count">
                {formatNumber(tab.count)}
              </span>
            </Button>
          ))}
        </nav>
        {inbox.mutationError && (
          <PageNotice tone="error">
            {t("notifications.error.action")}
          </PageNotice>
        )}
        {inbox.error ? (
          <PageState
            tone="error"
            title={t("notifications.error.title")}
            description={
              isAuthFailure(inbox.error)
                ? t("notifications.error.auth")
                : undefined
            }
            action={
              <Button onClick={inbox.retry}>{t("notifications.retry")}</Button>
            }
          />
        ) : inbox.loading ? (
          <PageState loading title={t("notifications.loading")} />
        ) : inbox.items.length === 0 ? (
          <NotificationsEmpty
            title={
              filter === "unread"
                ? t("notifications.empty.unread")
                : t("notifications.empty")
            }
          />
        ) : (
          <ul className="notifications-list">
            {inbox.items.map((item) => {
              const toggleLabel = item.readAt
                ? t("notifications.markUnread")
                : t("notifications.markRead");
              const deleteLabel = t("notifications.delete");
              return (
                <li
                  key={item.id}
                  className={`notifications-row${item.readAt ? "" : " is-unread"}`}
                >
                  <NotificationItem
                    item={item}
                    onOpen={(row) => {
                      if (!row.readAt) inbox.markRead(row.id);
                    }}
                  />
                  <div className="notifications-actions">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={toggleLabel}
                      title={toggleLabel}
                      onClick={() =>
                        item.readAt
                          ? inbox.markUnread(item.id)
                          : inbox.markRead(item.id)
                      }
                    >
                      {item.readAt ? (
                        <Mail aria-hidden="true" />
                      ) : (
                        <MailOpen aria-hidden="true" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={deleteLabel}
                      title={deleteLabel}
                      onClick={() =>
                        void remove(
                          item.id,
                          renderNotification(item, t, locale).title,
                        )
                      }
                    >
                      <Trash2 aria-hidden="true" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {inbox.hasMore && (
          <Button
            variant="secondary"
            onClick={inbox.loadMore}
            disabled={inbox.loadingMore}
          >
            {t("notifications.loadMore")}
          </Button>
        )}
      </PageSection>
    </PageLayout>
  );
}
